import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import { captureException, captureMessage, withScope } from '@sentry/react';

import { GraphQLError } from 'graphql';
import auth from './auth';
import config from './config';
import { onError } from '@apollo/client/link/error';

const fetchWithTokenRefresh = async (
  input: RequestInfo,
  init?: RequestInit,
) => {
  if (auth.isUserLoggedIn()) {
    try {
      await auth.refreshAccessTokenIfExpired();
    } catch (error) {
      captureException(error);
    }
  }

  return fetch(input, init);
};

const httpLink = createHttpLink({
  uri: config.graphqlURL,
  credentials: 'include',
  fetch: fetchWithTokenRefresh,
  fetchOptions: {
    mode: 'cors',
  },
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  withScope((scope) => {
    scope.setTransactionName(operation.operationName);
    scope.setContext('ApolloGraphQLOperation', {
      operationName: operation.operationName,
      variables: operation.variables,
      extensions: operation.extensions,
    });

    // eslint-disable-next-line no-unused-expressions
    graphQLErrors?.forEach((error) => {
      captureMessage(error.message, {
        level: 'error',
        fingerprint: ['{{ default }}', '{{ transaction }}'],
        contexts: {
          apolloGraphQLError: {
            error,
            message: error.message,
            extensions: error.extensions,
          },
        },
      });

      console.log(
        `[GraphQL error]: Message: ${error.message}, Location: ${error.locations}, Path: ${error.path}`,
      );
    });

    if (networkError) {
      captureMessage(networkError.message, {
        level: 'warning',
        contexts: {
          apolloNetworkError: {
            error: networkError,
            extensions: (networkError as any).extensions,
          },
        },
      });

      console.log(`[Network error]: ${networkError}`);
    }
  });
});

const client = new ApolloClient({
  name: 'JG.FrontEnd.TeamsCreation',
  link: ApolloLink.from([httpLink, errorLink]),
  cache: new InMemoryCache(),
});

export default client;

export function hasUnauthenticatedError(
  errors: Readonly<GraphQLError[]>,
): boolean {
  const error = errors?.find((e) => e.extensions?.code === 'UNAUTHENTICATED');

  return !!error;
}
